import {createContext} from 'react';
import {fromJS} from 'immutable';
import moment from 'moment';
import systemTimeOffsetStore from 'client/generic/SystemTimeOffset/SystemTimeOffset.store';
import makeConstants from 'lib/makeConstants';
import {getMandarkURL} from '@albert-io/environment';
import type {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';
import type {StudentModelV2} from 'resources/augmented/Student/StudentModel.v2';
import {BUILD_HEADER, genericMandarkRequest} from 'resources/mandark.resource';
import {ERROR_KIND, isTransportError, normalizeError} from 'lib/normalizeError';
import type {AssignmentModelV3} from '@albert-io/models';
import {hasZustand} from 'components/QuestionTypes/V2/useQuestionStores';
import {createEmptyGuess} from 'components/QuestionTypes/QuestionType.guess';

export interface AssignmentContextProps {
  assignment: any;
  classroom: ClassroomModelV1;
  student: StudentModelV2;
  status: any;
  isGraded: boolean;
  isOutOfTime: boolean;
  isAnswerable: boolean;
  isPastDue: boolean;
  isSavingDraftGuess: boolean;
  classroomId: string;
  setIsSavingDraftGuess: (value: boolean) => void;
  /** Saves pending annotation edits before a submit, after which the server refuses them. */
  flushAnnotations: () => Promise<void>;
  studentId: string;
}

export const AssignmentContext = createContext({} as AssignmentContextProps);

/**
 * Builds a display-only empty guess for a graded question that has no recorded guess.
 *
 * Legacy stores implement `buildEmptyGuess` (and some, e.g. TextHighlight, override it), so we
 * defer to the store for those. Zustand-backed question types (item-bank, ordering) don't
 * implement it — calling it threw "buildEmptyGuess is not a function" while navigating a graded
 * assignment — so synthesize the equivalent empty guess directly for them.
 *
 * Returns `null` when there is no question to build for: a graded assignment whose
 * questions failed to load hands this an `undefined` question.
 */
export function buildEmptyGuessForQuestion(question: any) {
  if (!question) {
    return null;
  }
  const store = question.getStore();
  return hasZustand(store) ? createEmptyGuess(question) : store.buildEmptyGuess(question);
}

/**
 * @typedef {import('../../resources/augmented/Assignment/AssignmentModel.v3').AssignmentModelV3} Assignment
 * @typedef {import('moment').Moment} Moment
 */

export const correctAnswerSettings = makeConstants(
  'showAfterAll',
  'showAfterEach',
  'showAfterDueDate'
);

export const statusTypes = makeConstants('notStarted', 'inProgress', 'submitted');

/**
 * @param root0
 * @param root0.assignment
 * @returns {{
 *   getConfirmedSentEmailAt: () => ?Moment,
 *   getDeletedAt: () => ?Moment,
 *   getSentEmailAt: () => ?Moment,
 *   getStartTime: () => ?Moment,
 *   getSubmitted: () => ?Moment
 * } | undefined}
 */
function getStudentRelationshipData({assignment}) {
  const student = assignment?.getStudents?.().first(); // Add undefined and empty checks
  if (!student || typeof student.getAssignmentRelationships !== 'function') return; // Check if student is null/undefined/empty
  return student.getAssignmentRelationships(assignment.getId());
}

/**
 * @param root0
 * @param root0.assignment
 * @returns {string}
 */
export function getSubmissionStatus({assignment}) {
  const relationshipData = getStudentRelationshipData({assignment});
  const isSubmitted =
    assignment.getMeta().isStudentSubmitted() || !!relationshipData?.getSubmitted?.();
  const isStarted = !!relationshipData?.getStartTime?.(); // Add undefined/empty check for startime

  if (isSubmitted) {
    return statusTypes.submitted;
  }
  if (!isStarted) {
    return statusTypes.notStarted;
  }
  return statusTypes.inProgress;
}

/**
 * Graded means that the student is able to see how they did in their assignment, and they can also see
 * the explanation of all questions, even ones they did not answer.
 *
 * @param root0
 * @param root0.assignment
 * @param root0.classroomId
 * @param root0.studentId
 * @returns {boolean}
 */
export function isGraded({assignment, classroomId, studentId}) {
  const submissionStatus = getSubmissionStatus({assignment});
  const correctAnswerSetting = assignment.getCorrectAnswerSettingFromSettings({
    classroomId,
    studentId
  });

  /**
   * If the assignment is `submitted`, explanations are _available_ if the assignment is not `showAfterDueDate`,
   * or if the due date is in the past.
   */
  if (
    submissionStatus === statusTypes.submitted &&
    (correctAnswerSetting !== correctAnswerSettings.showAfterDueDate ||
      isDueDatePast({assignment, classroomId, studentId}))
  ) {
    return true;
  }

  const allowsLateSubmissions = assignment.getIsAllowLateSubmissionsFromSettings({
    classroomId,
    studentId
  });
  const pastDue = isPastDue({assignment, classroomId, studentId});
  /**
   * If the assignment is `pastDue`, explanations are _available_ if the assignment is not `allowsLateSubmissions`
   */
  if (pastDue && !allowsLateSubmissions) {
    return true;
  }

  return false;
}

/**
 * Provides the data required to render     an assignment's details
 *
 * @param root0
 * @param root0.assignment
 * @returns {{
 *   isStarted: boolean,
 *   startTime: *,
 *   isSubmitted: boolean,
 *   submittedAt: *,
 *   status: string
 * }}
 */
export function getStudentAssignmentData({assignment}) {
  const student = assignment.getStudents().first();
  const assignmentMeta = assignment.getMeta();
  const relationshipData = student?.getAssignmentRelationships?.(assignment.getId());
  const submittedAt = relationshipData?.getSubmitted?.();
  const isSubmitted = submittedAt !== null && submittedAt !== undefined;
  const status = getSubmissionStatus({assignment});
  const classroom = assignment.getStudentClassrooms().isEmpty()
    ? null
    : assignment.getClassroomWithLatestAssignmentDueDate();
  const questionCount = assignmentMeta.getCountOfQuestions();
  const startTime = relationshipData?.getStartTime?.();
  const isStarted = startTime !== null && startTime !== undefined;
  const classroomId = classroom?.getId?.();
  const studentId = student?.getId?.();
  return {
    isStarted,
    startTime,
    isSubmitted,
    submittedAt,
    status,
    classroom,
    questionCount,
    student,
    isGraded: isGraded({assignment, classroomId, studentId})
  };
}

/**
 * Provides the data required to render an assignment's details
 *
 * @param root0
 * @param root0.assignment
 * @returns {*}
 */
export function getStudentStartTime({assignment}) {
  const startTime = getStudentRelationshipData({assignment})?.getStartTime?.(); // Add undefined/empty check for startime
  return startTime ? moment(startTime) : null;
}

export async function startAssignment({assignment}) {
  const student = assignment.getStudents().first();
  const assignmentId = assignment.getId();
  return student
    .addRelationship({
      type: 'assignments_v3',
      relation: [assignmentId],
      customMeta: fromJS({
        [assignmentId]: {
          start_time: 'now'
        }
      })
    })
    .save();
}

export const EXIT_BEACON_LOCAL_STORAGE_PREFIX = 'lockdown:exit:';
/** Marker considered "owned by the beacon" within this window. Generous so a
 *  slow reload still suppresses the on-return submit; well under any real
 *  session_interrupted delay. */
export const EXIT_BEACON_MARKER_TTL_MS = 60_000;

export function getExitBeaconMarkerKey(assignmentId: string): string {
  return `${EXIT_BEACON_LOCAL_STORAGE_PREFIX}${assignmentId}`;
}

/** Best-effort submit on `pagehide` via fetch(keepalive). Uses
 *  `session_interrupted` to match the on-return path; writes a short
 *  localStorage marker so the on-return duplicate skips (see #17491). */
export function sendExitBeacon({assignment}): boolean {
  if (typeof fetch === 'undefined') return false;

  const student = assignment?.getStudents?.().first?.();
  const studentId = student?.getId?.();
  const assignmentId = assignment?.getId?.();
  if (!studentId || !assignmentId) return false;

  // Marker first: must be visible to a quick reload even if the fetch
  // below is still in flight. localStorage can throw in Safari private mode.
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(getExitBeaconMarkerKey(assignmentId), String(Date.now()));
    }
  } catch {
    // ignore
  }

  const url = getMandarkURL(`/students_v2/${studentId}/relationships/assignments_v3`);
  const body = JSON.stringify({
    data: [
      {
        id: assignmentId,
        type: 'assignments_v3',
        meta: {submitted: 'now', lockdown_auto_submit_reason: 'session_interrupted'}
      }
    ]
  });

  try {
    void fetch(url, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
      headers: BUILD_HEADER(),
      body
    }).catch(() => {
      // fire-and-forget; suppress unhandled rejection during unload
    });
    return true;
  } catch {
    return false;
  }
}

const inFlightSubmits = new Map<string, Promise<unknown>>();

/** Test-only: forgets in-flight submits between tests. */
export function clearInFlightSubmits() {
  inFlightSubmits.clear();
}

/**
 * Submits the assignment for its first student. Every caller shares one
 * student model, which allows one pending save at a time, so a call made while
 * a submit for the same assignment and student is in flight joins that submit
 * and gets its result. A joining caller's `lockdownAutoSubmitReason` is not
 * sent; the server would deny a second submit anyway.
 */
export async function submitAssignment({assignment, lockdownAutoSubmitReason = null}) {
  const student = assignment.getStudents().first();
  const assignmentId = assignment.getId();
  const key = `${assignmentId}:${student.getId()}`;
  const inFlight = inFlightSubmits.get(key);
  if (inFlight) return inFlight;

  const meta: Record<string, unknown> = {submitted: 'now'};
  if (lockdownAutoSubmitReason) {
    meta.lockdown_auto_submit_reason = lockdownAutoSubmitReason;
  }
  const sendSubmit = async () =>
    student
      .addRelationship({
        type: 'assignments_v3',
        relation: [assignmentId],
        customMeta: fromJS({
          [assignmentId]: meta
        })
      })
      .save();
  const tracked = sendSubmit().finally(() => {
    if (inFlightSubmits.get(key) === tracked) inFlightSubmits.delete(key);
  });
  inFlightSubmits.set(key, tracked);
  return tracked;
}

/** Delay before the single retry in `submitAssignmentOnTimeout`. Time-out
 *  submission happens at an exam-critical moment, so we give a transient
 *  failure (brief network blip, momentary backend hiccup) one chance to clear
 *  before surfacing the error. */
export const TIMEOUT_SUBMIT_RETRY_DELAY_MS = 2_000;

/**
 * Submits an assignment because its time limit has expired.
 *
 * Intentionally a thin wrapper around `submitAssignment` that does NOT pass a
 * `lockdownAutoSubmitReason`. Time-out is a normal submission trigger, not a
 * lockdown auto-submit event — keeping `lockdown_auto_submit_reason` null
 * means the post-submit UI shows the regular results page, with no violation
 * framing or auto-submit modal.
 *
 * Used by `handleOutOfTime` to pre-empt `LockdownModeProvider`'s
 * session-interrupted guard: by the time the practice view re-renders after
 * the post-submit invalidate, status === submitted, so the guard
 * short-circuits and the provider disarms cleanly.
 *
 * Because this fires at an exam-critical moment, it retries once after a short
 * delay when the first attempt fails with a network, timeout, or server error.
 * Any other failure, and a second failure, propagates to the caller so it can
 * still be reported.
 *
 * @see https://github.com/albert-io/nexus/issues/17544
 */
export function submitAssignmentOnTimeout({assignment}) {
  return submitAssignmentOnLockdown({assignment, lockdownAutoSubmitReason: null});
}

/**
 * Submits once more after a short delay when the first attempt fails with a
 * shape a retry can help (network, timeout, 5xx). A 4xx would repeat identically.
 */
export async function submitAssignmentOnLockdown({assignment, lockdownAutoSubmitReason}) {
  try {
    return await submitAssignment({assignment, lockdownAutoSubmitReason});
  } catch (error) {
    const {kind} = normalizeError(error);
    const retryable =
      isTransportError(error) ||
      kind === ERROR_KIND.NETWORK ||
      kind === ERROR_KIND.TIMEOUT ||
      kind === ERROR_KIND.SERVER_ERROR;
    if (!retryable) {
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, TIMEOUT_SUBMIT_RETRY_DELAY_MS));
    return submitAssignment({assignment, lockdownAutoSubmitReason});
  }
}

/**
 * Fresh read of the student's submitted flag, bypassing cached models: a failed
 * auto-submit must distinguish "submit failed" from "already recorded".
 */
export async function fetchAssignmentSubmitted({assignmentId, studentId}) {
  const assignment: AssignmentModelV3 | null | undefined = await genericMandarkRequest(
    'get',
    {
      resourcePath: ['assignments_v3', assignmentId],
      customQuery: {
        with_meta: 'assignment_v3',
        meta: {context: {student: {id: studentId}, user: {id: studentId}}}
      }
    },
    undefined,
    {suppressGlobalErrorToast: true}
  );
  return assignment?.getMeta?.().isStudentSubmitted?.() === true;
}

/**
 * Given a list of question sets with questions included, returns the first question that
 * does not have a guess, and its question set. If all questions have guesses or if the assignment
 * is submitted, it returns the first question and its set.
 *
 * @param root0
 * @param root0.questionSets
 * @param root0.guesses
 * @param root0.isGraded
 */
export function getInitialSetAndQuestion({questionSets, guesses, isGraded: isGradedValue}) {
  let initialSet;
  let initialQuestion;

  if (!isGradedValue) {
    initialSet = questionSets.find(
      (set) => {
        const unansweredQuestion = set.getQuestions().find((question) => {
          return guesses.some((guess) => guess.getQuestion().getId() !== question.getId());
        });

        if (unansweredQuestion) {
          initialQuestion = unansweredQuestion;
          return true;
        }

        return false;
      },
      null,
      questionSets.first()
    );
  } else {
    initialSet = questionSets.first();
    initialQuestion = initialSet?.getQuestions?.()?.first?.();
  }

  return {
    questionSet: initialSet,
    question: initialQuestion || initialSet?.getQuestions?.()?.first?.()
  };
}

/**
 * Counts what a settled question-sets resource actually delivered. The two counts
 * separate "no question sets at all" from "sets arrived but their questions include
 * came back empty" — different upstream failures that would otherwise be
 * indistinguishable in error reporting. A set without a readable questions
 * collection counts as a set with zero questions; an absent resource counts as
 * nothing delivered.
 */
export function getReturnedQuestionCounts(
  questionSets:
    | Iterable<{getQuestions?: () => {size: number} | null | undefined}>
    | null
    | undefined
): {returnedQuestionCount: number; returnedQuestionSetCount: number} {
  let returnedQuestionCount = 0;
  let returnedQuestionSetCount = 0;
  for (const questionSet of questionSets ?? []) {
    returnedQuestionSetCount += 1;
    returnedQuestionCount += questionSet.getQuestions?.()?.size ?? 0;
  }
  return {returnedQuestionCount, returnedQuestionSetCount};
}

/**
 * An assignment is past due if it's not submitted and the due date
 * is in the past, or if was submitted after its due date.
 *
 * @param root0
 * @param root0.assignment
 * @param root0.classroomId
 * @param root0.studentId
 */
export function isPastDue({assignment, classroomId, studentId}) {
  const now = systemTimeOffsetStore.getCurrentTime();
  const dueDate = assignment.getDueDateFromSettings({
    classroomId,
    studentId
  });

  // If there's no due date or it's invalid, the assignment is not past due
  if (!dueDate || !moment.isMoment(dueDate) || typeof dueDate.isAfter !== 'function') {
    return false;
  }

  // If the assignment's due date is in the future, it's not past due
  if (dueDate.isAfter(now)) {
    return false;
  }

  // If the assignment was submitted before the due date, it's not past due
  const relationshipData = getStudentRelationshipData({assignment});
  const submittedAt = relationshipData?.getSubmitted?.();
  if (submittedAt && moment(submittedAt).isBefore(dueDate)) {
    return false;
  }

  return true;
}

/**
 * Returns whether or not guesses are able to be placed on an assignment, either now or in
 * the future. This includes assignments which are not started as long as guesses will eventually
 * be able to placed against them.
 *
 * @param root0
 * @param root0.assignment
 * @param root0.classroomId
 * @param root0.studentId
 */
export function isAnswerable({assignment, classroomId, studentId}) {
  if (
    isGraded({assignment, classroomId, studentId}) ||
    getSubmissionStatus({assignment}) === statusTypes.submitted
  ) {
    return false;
  }

  const secondsLeft = getSecondsLeftInTimeLimit({assignment, classroomId, studentId});
  if (secondsLeft !== null && secondsLeft <= 0) {
    return false;
  }

  // If we haven't submitted the assignment, and it was due in the past,
  // and it does not allow late submissions, we can't place guesses
  if (
    isDueDatePast({assignment, classroomId, studentId}) &&
    !assignment.getIsAllowLateSubmissionsFromSettings({classroomId, studentId})
  ) {
    return false;
  }

  return true;
}

/**
 * @param root0
 * @param root0.assignment
 * @param root0.classroomId
 * @param root0.studentId
 */
export function isDueDatePast({assignment, classroomId, studentId}) {
  const now = systemTimeOffsetStore.getCurrentTime();
  const dueDate = assignment.getDueDateFromSettings({classroomId, studentId});
  // If there's no due date or it's invalid, the due date is not past
  if (!dueDate || !moment.isMoment(dueDate)) {
    return false;
  }
  return dueDate.isBefore(now);
}

/**
 * If the assignment has a time limit and it has been started, this returns the amount of seconds left
 * until that time limit has been reached. Otherwise, it returns null.
 *
 * @param root0
 * @param root0.assignment
 * @param root0.classroomId
 * @param root0.studentId
 * @returns {?number}
 */
export function getSecondsLeftInTimeLimit({assignment, classroomId, studentId}) {
  const startTime = getStudentStartTime({assignment});
  const timeLimit = assignment.getTimeLimitFromSettings({classroomId, studentId});

  if (!timeLimit || !startTime) {
    return null;
  }

  const outOfTimeDate = moment(startTime).add(timeLimit, 'minutes');
  const now = systemTimeOffsetStore.getCurrentTime();
  return outOfTimeDate.diff(now, 'seconds');
}

export function isOutOfTime({assignment, classroomId, studentId}) {
  const secondsLeft = getSecondsLeftInTimeLimit({assignment, classroomId, studentId});
  return secondsLeft !== null && secondsLeft <= 0;
}

interface QuestionAnsweredArgs {
  hasSubmittedGuess: boolean;
  /** False when the question's store holds no draft, leaving the load-time snapshot to stand. */
  hasLoadedDraft: boolean;
  isLoadedDraftEmpty: boolean;
  /** Whether a non-empty draft existed server-side when the assignment was opened. */
  hadDraftAtLoad: boolean;
}

/**
 * Whether a question counts toward assignment progress and the all-answered submit gate.
 *
 * A submitted guess is definitive — clearing a draft afterwards must not un-answer the question,
 * which is what previously dropped free-response questions from the count. A draft only decides
 * the answer for a question that was never submitted, and only once its store has loaded one.
 */
export function isQuestionAnswered({
  hasSubmittedGuess,
  hasLoadedDraft,
  isLoadedDraftEmpty,
  hadDraftAtLoad
}: QuestionAnsweredArgs): boolean {
  if (hasSubmittedGuess) {
    return true;
  }

  return hasLoadedDraft ? !isLoadedDraftEmpty : hadDraftAtLoad;
}

/** The only part of a guess result this module reads: the placed guess, and its grade. */
type GradedGuessResult = {get: (key: 'guess') => {getGrade: () => number}};

interface AccuracyArgs {
  /** `StudentAssignment.grade`, withheld by the server until the assignment closes. */
  grade: number | null | undefined;
  isAssignmentGraded: boolean;
  correctGuessCount: number;
  totalGuessCount: number;
  isPartialCreditEnabled: boolean;
  guessResultsMap: {
    size: number;
    reduce: (
      reducer: (accumulator: number, guessResult: GradedGuessResult) => number,
      seed: number
    ) => number;
  };
}

/**
 * The figure the practice view shows as "Accuracy", or null when it cannot be determined.
 *
 * A graded assignment's figure only ever comes from the server. The local tally counts a
 * rubric guess as fully correct whenever it earned any credit, so it must not stand in when
 * the grade is missing — it exists solely for an in-progress assignment, which has no
 * released grade to read.
 */
export function getAssignmentAccuracy({
  grade,
  isAssignmentGraded,
  correctGuessCount,
  totalGuessCount,
  isPartialCreditEnabled,
  guessResultsMap
}: AccuracyArgs): number | null {
  if (grade != null) {
    return grade;
  }

  if (isAssignmentGraded) {
    return null;
  }

  const localAccuracy = isPartialCreditEnabled
    ? guessResultsMap.reduce(
        (accumulator, guessResult) => accumulator + guessResult.get('guess').getGrade(),
        0
      ) / guessResultsMap.size
    : correctGuessCount / totalGuessCount;

  return Number.isNaN(localAccuracy) ? 0 : localAccuracy;
}
